import {Stack, tokens} from '@pleo-io/telescope'
import * as ReachSlider from '@reach/slider'
import React from 'react'
import {Helmet} from 'react-helmet'
import styled, {css} from 'styled-components'
import {v4 as uuidv4} from 'uuid'

import Image from '@helios/shared/components/image'
import Input from '@helios/shared/components/input'
import {Label} from '@helios/shared/styles/inputs'
import {focusStyles} from '@helios/shared/styles/utils'
import {radii, colors} from '@helios/shared/theme'
import ImageSource from '@helios/shared/types/image'
import mq from '@helios/shared/utils/media-query'

/**
 * Returns a function which returns a boolean indicating whether or not the
 * given value is inclusively within the start and end values
 */
const isWithinRange = (value: number) => (start: number, end: number) =>
    value >= start && value <= end

export interface Props {
    label?: string
    value: number
    min?: number
    max?: number
    step?: number
    onChange: NonNullable<ReachSlider.SliderInputProps['onChange']>
    onFocus?: () => void
    icons?: [ImageSource, ImageSource, ImageSource]
    withNumberInput?: boolean
    disabled?: boolean
}

const Slider = ({
    label,
    icons,
    withNumberInput,
    onChange,
    onFocus,
    min = 0,
    max = 100,
    ...props
}: Props) => {
    const getValidValue = (value: number) => {
        const step = props.step ?? 1
        // If steps are defined, we need to round to the nearest stepped value
        const roundedValue = Math.round(value / step) * step
        // Constrains number to min and max values
        const pinnedValue = Math.min(Math.max(roundedValue, min), max)

        return pinnedValue
    }

    const getIconFromValue = (value: number) => {
        if (!icons) {
            return null
        }

        const [smallIcon, mediumIcon, largeIcon] = icons

        const thirdOfRange = (max - min) / 3
        const valueIsBetween = isWithinRange(getValidValue(value))

        // Changes the icon for every third of the slider range
        switch (true) {
            case valueIsBetween(min, thirdOfRange):
                return smallIcon
            case valueIsBetween(thirdOfRange, thirdOfRange * 2):
                return mediumIcon
            case valueIsBetween(thirdOfRange * 2, max):
                return largeIcon
            default: {
                return null
            }
        }
    }

    const [activeIcon, setActiveIcon] = React.useState<ImageSource | null>(
        getIconFromValue(props.value)
    )
    const [isFocused, setIsFocused] = React.useState(false)

    const handleFocusChange = (e: React.FocusEvent<HTMLDivElement>) => {
        setIsFocused(Object.is(e.target, document.activeElement))

        // handleFocusChange() runs on blur, and we only want
        // onFocus() should only run when the element is focused, hence this if statement
        if (onFocus && e.type === 'focus') {
            onFocus()
        }
    }

    const handleChange: Props['onChange'] = (newValue, handleChangeProps) => {
        onChange(newValue, handleChangeProps)

        setActiveIcon(getIconFromValue(newValue))
    }

    const handleBlur = (value: number) => {
        onChange(getValidValue(value))
    }

    const id = uuidv4()

    return (
        <>
            {/* Preload icons to make sure they show as soon as you interact with the slider */}
            <Helmet>
                {icons?.map((icon) => (
                    <link key={icon.url} rel="preload" href={icon.url} as="image" />
                ))}
            </Helmet>

            <Stack space={20} stretch>
                {label && <Label id={id}>{label}</Label>}

                <Wrapper
                    $withNumberInput={withNumberInput}
                    $disabled={props.disabled}
                    onFocus={handleFocusChange}
                    onBlur={handleFocusChange}
                >
                    {activeIcon && icons && <MobileIcon image={activeIcon} />}

                    <SliderInput
                        {...props}
                        min={min}
                        max={max}
                        handleAlignment="contain"
                        onChange={handleChange}
                    >
                        <SliderTrack>
                            <SliderRange />
                            <SliderHandle
                                $iconUrl={activeIcon?.url || ''}
                                data-focus={isFocused ? '' : undefined}
                            />
                        </SliderTrack>
                    </SliderInput>

                    {withNumberInput && (
                        <StyledInput
                            type="number"
                            min={min}
                            max={max}
                            disabled={props.disabled}
                            step={props.step}
                            value={props.value.toString()}
                            onFocus={handleFocusChange}
                            onChange={(value) => handleChange(Number(value))}
                            onBlur={(e) => {
                                handleFocusChange(e)
                                handleBlur(Number(e.target.value))
                            }}
                            aria-labelledby={id}
                        />
                    )}
                </Wrapper>
            </Stack>
        </>
    )
}

const mobileBreakpoint = 'sm'

const StyledInput = styled(Input)`
    /* Override the default disabled Input styles so we can style this whole component collectively */
    :disabled {
        color: ${colors.textInput.default.foreground};
        background: ${colors.textInput.default.background};
    }

    :focus {
        ${focusStyles()}
        border-radius: ${tokens.arc4};
    }
`

interface WrapperProps {
    $withNumberInput?: Props['withNumberInput']
    $disabled?: Props['disabled']
}

const Wrapper = styled.div<WrapperProps>(({$withNumberInput, $disabled}) => {
    const withNumberInputStyles = css`
        ${mq.min(mobileBreakpoint)} {
            grid-template-columns: 1fr 100px;

            input[type='number'] {
                text-align: center;
            }
        }
    `

    const disabledStyles = css`
        opacity: 0.25;
    `

    return css`
        display: grid;
        width: 100%;
        row-gap: ${tokens.spacing10};
        column-gap: ${tokens.spacing24};
        align-items: center;
        justify-items: center;

        ${$withNumberInput && withNumberInputStyles}
        ${$disabled && disabledStyles}
    `
})

const SliderInput = styled(ReachSlider.SliderInput)`
    &[data-reach-slider-input] {
        width: 100%;
        max-width: 100%;

        height: 8px;
        padding-top: 30px;
        padding-bottom: 30px;

        ${mq.min(mobileBreakpoint)} {
            height: 4px;
        }
    }

    &[data-disabled] {
        pointer-events: none;
        cursor: not-allowed;
        opacity: unset;
    }
`

const SliderTrack = styled(ReachSlider.SliderTrack)`
    width: 100%;
    height: inherit;
    background: ${tokens.shade300};
    border-radius: 0;
`

const SliderRange = styled(ReachSlider.SliderTrackHighlight)`
    background: ${tokens.shade900};
`

interface SliderHandleProps {
    $iconUrl: string
}

const SliderHandle = styled(ReachSlider.SliderHandle)<SliderHandleProps>(({$iconUrl}) => {
    return css`
        top: 50%;
        width: 24px;
        height: 24px;
        background: ${tokens.shade000};
        border: 2px solid ${tokens.shade900};
        border-radius: ${radii.circle};
        transform: translateY(-50%);

        ${mq.min(mobileBreakpoint)} {
            width: 16px;
            height: 16px;
        }

        &[data-focus] {
            border: 2px solid ${tokens.blue700};
            outline: none;
        }

        /* Invisible larger touch target to improve a11y */
        :before {
            position: absolute;
            top: 50%;
            left: 50%;
            width: 44px;
            height: 44px;
            content: '';
            background: transparent;
            border-radius: inherit;
            transform: translate(-50%, -50%);
        }

        /* Icon that displays when focussing the handle or the number input */
        &[data-focus]:after {
            position: absolute;
            top: calc(100% + ${tokens.spacing16});
            left: 50%;
            display: block;
            width: 45px;
            height: 45px;
            pointer-events: none;
            content: '';
            background-image: url('${$iconUrl}');
            background-repeat: no-repeat;
            background-position: center;
            background-size: contain;
            transition: background ${tokens.smoothInOut};
            transform: translateX(-50%);

            ${mq.max(mobileBreakpoint)} {
                display: none;
            }
        }
    `
})

const MobileIcon = styled(Image)`
    width: 80px;
    height: 80px;

    ${mq.min(mobileBreakpoint)} {
        display: none;
    }
`

export default Slider
